/**
 * Text matching utilities — shared by spotlight search, highlight matching,
 * and split-view click navigation.
 *
 * Centralizes all text-search logic so there's a single place to fix
 * whitespace normalization, case folding, and fallback strategies.
 */

/**
 * Find ALL occurrences of `needle` in `haystack` (case-insensitive),
 * returning the char index of each hit.
 */
export function findAllOccurrences(haystack: string, needle: string): number[] {
    if (!needle || !haystack) return [];
    const results: number[] = [];
    const lowerHay = haystack.toLowerCase();
    const lowerNeedle = needle.toLowerCase();
    let pos = 0;
    while (pos < lowerHay.length) {
        const idx = lowerHay.indexOf(lowerNeedle, pos);
        if (idx === -1) break;
        results.push(idx);
        pos = idx + 1; // allow overlapping matches
    }
    return results;
}

/** Collapse whitespace and lowercase — for tolerant matching when exact indexOf fails. */
export function normalizeWhitespace(s: string): string {
    return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Whitespace-tolerant search: normalize both needle and haystack,
 * find match positions in the normalized haystack, then map back
 * to approximate positions in the original haystack.
 *
 * Returns original-haystack char indices (approximate — for line mapping only).
 */
export function findAllNormalized(haystack: string, needle: string): number[] {
    if (!needle || !haystack) return [];
    const normHay = normalizeWhitespace(haystack);
    const normNeedle = normalizeWhitespace(needle);
    if (!normNeedle) return [];

    const normHits: number[] = [];
    let pos = 0;
    while (pos < normHay.length) {
        const idx = normHay.indexOf(normNeedle, pos);
        if (idx === -1) break;
        normHits.push(idx);
        pos = idx + 1;
    }
    if (normHits.length === 0) return [];

    // Map normalized char positions back to original haystack positions.
    // Walk original haystack char-by-char, tracking the corresponding
    // normalized position (whitespace runs collapse to a single space).
    const normToRaw: number[] = []; // normToRaw[normIdx] = rawIdx
    const lowerHay = haystack.toLowerCase();
    let normIdx = 0;
    // skip leading whitespace to match trim()
    let rawStart = 0;
    while (rawStart < lowerHay.length && /\s/.test(lowerHay[rawStart])) rawStart++;

    for (let rawIdx = rawStart; rawIdx < lowerHay.length && normIdx < normHay.length; rawIdx++) {
        const ch = lowerHay[rawIdx];
        if (/\s/.test(ch)) {
            // In normalized form, this whitespace run became a single space
            if (normIdx < normHay.length && normHay[normIdx] === ' ') {
                normToRaw[normIdx] = rawIdx;
                normIdx++;
            }
            // Skip remaining whitespace in raw
            while (rawIdx + 1 < lowerHay.length && /\s/.test(lowerHay[rawIdx + 1])) rawIdx++;
        } else {
            normToRaw[normIdx] = rawIdx;
            normIdx++;
        }
    }

    return normHits.map(ni => normToRaw[ni] ?? 0);
}

/** Convert a char index in a body text string to a line index (0-based). */
export function charIndexToLine(bodyText: string, charIdx: number): number {
    return (bodyText.slice(0, charIdx).match(/\n/g) || []).length;
}

/** Strip transcript line-number gutters from each line: "  14   Q. Did you" → "Q. Did you" */
export function stripGutter(s: string): string {
    return s.split('\n').map(ln => ln.replace(/^\s*\d{1,3}\s{1,6}/, '')).join('\n');
}

/** Strip standalone line numbers the LLM may have embedded in a quote. */
export function stripEmbeddedLineNumbers(s: string): string {
    let result = s.replace(/^(\s*\d{1,3}\s+)+/, '');
    result = result.replace(/(?<=[.?!])\s+\d{1,3}(?=\s+[QA]\s)/gi, '');
    return result;
}

/** Strip gutters + embedded line numbers + collapse whitespace + lowercase. */
export function fullyNormalize(s: string): string {
    let result = stripGutter(s);
    result = result.toLowerCase().replace(/\s+/g, ' ').trim();
    result = stripEmbeddedLineNumbers(result);
    return result.trim();
}

/**
 * Find needle in haystack using full normalization (gutter + whitespace + case).
 * Returns approximate char positions in the ORIGINAL haystack for line/page mapping.
 */
export function findFullyNormalized(haystack: string, needle: string): number[] {
    if (!needle || !haystack) return [];
    const normHay = fullyNormalize(haystack);
    const normNeedle = fullyNormalize(needle);
    if (!normNeedle || normNeedle.length < 3) return [];

    const hits: number[] = [];
    let pos = 0;
    while (pos < normHay.length) {
        const idx = normHay.indexOf(normNeedle, pos);
        if (idx === -1) break;
        hits.push(idx);
        pos = idx + 1;
    }
    if (hits.length === 0) return [];

    const normToRaw: number[] = [];
    const stripped = stripGutter(haystack);
    const lower = stripped.toLowerCase();
    let ni = 0;
    let rawStart = 0;
    while (rawStart < lower.length && /\s/.test(lower[rawStart])) rawStart++;
    for (let ri = rawStart; ri < lower.length && ni < normHay.length; ri++) {
        if (/\s/.test(lower[ri])) {
            if (ni < normHay.length && normHay[ni] === ' ') {
                normToRaw[ni] = ri;
                ni++;
            }
            while (ri + 1 < lower.length && /\s/.test(lower[ri + 1])) ri++;
        } else {
            normToRaw[ni] = ri;
            ni++;
        }
    }
    return hits.map(h => normToRaw[h] ?? 0);
}

/**
 * Strip transcript page headers, footers, line numbers, and reporter stamps
 * from text. Works with both newline-separated text (page content) and flat
 * text (LLM-extracted quotes that lost newlines).
 *
 * Shared by the text view (useLineSpotlight), PDF view (singlePageContent),
 * and findQuoteInBody (strategy 5). Consolidates the formerly-independent
 * stripNonTestimony and strategy-3 number stripping logic.
 */
export function stripTranscriptJunk(text: string): string {
    // If the text has no newlines, try splitting on inline line-number
    // boundaries: "...word 20 Next word" → ["...word", "20 Next word"]
    let lines: string[];
    if (text.includes('\n')) {
        lines = text.split('\n');
    } else {
        // Split before numbers that precede an uppercase letter (likely line numbers)
        lines = text.split(/(?<=\S)\s+(?=\d{1,3}\s{1,4}[A-Z])/).flatMap(s => s.split('\n'));
    }

    const kept = lines.filter(line => {
        const trimmed = line.trim();
        if (!trimmed) return false;
        // Standalone numbers (page numbers, line numbers)
        if (/^\d{1,4}$/.test(trimmed)) return false;
        // "Page N"
        if (/^\s*page\s+\d+\s*$/i.test(trimmed)) return false;
        // Phone numbers
        if (/\d{3}[.-]\d{3}[.-]\d{4}/.test(trimmed)) return false;
        // URLs (http/https/www and common TLDs)
        if (/https?:\/\/|WWW\.|\.COM|\.ORG|\.NET|\.EDU|\.GOV/i.test(trimmed)) return false;
        // Confidentiality markings / source stamps
        if (/^\s*(?:Confidential|CONFIDENTIAL)\s*(?:Source|Treatment|—|:)/i.test(trimmed)) return false;
        // Document IDs / Bates numbers (e.g. TEVA_MDL_A_13761973, TGOS_VA4_MDL_10865967)
        if (/^[A-Z0-9_]{8,}$/.test(trimmed)) return false;
        // Reporter stamps
        if (/^\s*(?:PLANET DEPOS|VERITEXT|ESQUIRE|HUSEBY|US LEGAL)\b/i.test(trimmed)) return false;
        // "Transcript of..." / "Conducted on..." / "Deposition of..." headers
        if (/^(?:Transcript|Deposition|Conducted|Examination)\s+(?:of|on|by)\b/i.test(trimmed)) return false;
        // ALL CAPS short lines — likely case captions or running headers
        if (trimmed.length < 60 && trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed)) return false;
        // Strip leading line-number gutter, keep if remaining content ≥ 3 chars
        const withoutLineNum = trimmed.replace(/^\d{1,3}\s{1,6}/, '');
        return withoutLineNum.length >= 3;
    }).map(line => line.replace(/^\s*\d{1,3}\s{1,6}/, '').trim());

    return kept.join(' ').replace(/\s{2,}/g, ' ').trim();
}

/**
 * Find the char offset of a quote in a body text, using a cascade of
 * increasingly tolerant matching strategies. Returns -1 if not found.
 *
 * Shared by the text view (useLineSpotlight) and the PDF view
 * (extractPdfSearchText) so both use the same matching logic.
 */
export function findQuoteInBody(body: string, quote: string): number {
    if (!body || !quote) return -1;

    // 1. Exact
    let idx = body.indexOf(quote);
    if (idx !== -1) return idx;

    // 2. Case-insensitive
    idx = body.toLowerCase().indexOf(quote.toLowerCase());
    if (idx !== -1) return idx;

    // 3. Whitespace-normalized
    const normHits = findAllNormalized(body, quote);
    if (normHits.length > 0) return normHits[0];

    // 4. Fully normalized (strips gutters + embedded line numbers)
    const fullHits = findFullyNormalized(body, quote);
    if (fullHits.length > 0) return fullHits[0];

    return -1;
}
